import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import API_BASE_URL from '../config';
import {
  FaGraduationCap,
  FaStethoscope,
  FaTools,
  FaHandshake,
  FaBullhorn,
  FaRocket,
  FaArrowRight,
  FaTh,
  FaSearch,
  FaCogs,
  FaCheckCircle,
  FaGlobe,
  FaBuilding,
  FaChartLine,
  FaLightbulb,
  FaStar,
} from 'react-icons/fa';
import './Services.css';

const getServiceIcon = (title = '', icon = '') => {
  if (icon && icon.length <= 4) return <span style={{ fontSize: 28 }}>{icon}</span>;

  const t = title.toLowerCase();
  if (t.includes('educat')) return <FaGraduationCap />;
  if (t.includes('medic') || t.includes('health')) return <FaStethoscope />;
  if (t.includes('construct') || t.includes('material')) return <FaTools />;
  if (t.includes('job') || t.includes('consult')) return <FaHandshake />;
  if (t.includes('market') || t.includes('ad')) return <FaBullhorn />;
  if (t.includes('future') || t.includes('venture')) return <FaRocket />;
  return <FaCogs />;
};

const getServicePath = (title = '') => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `/services/${slug}`;
};

const Services = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const { ref: cardsRef, inView: cardsVisible } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/public/landing/`);
        const data = res.data || {};

        const mappedServices = (data.services || []).map((s) => {
          const title = s.title || 'Service';
          return {
            id: s.id,
            title,
            desc: s.description || '',
            iconNode: getServiceIcon(title, s.icon),
            category: title,
            path: getServicePath(title),
            color: s.color || '#c9a227',
            features: ['Professional Solutions', 'Trusted Delivery', 'End-to-End Support'],
          };
        });

        setServices(mappedServices);
        setPartners((data.partners || []).map((p) => p.name || p).filter(Boolean));
      } catch (err) {
        console.error('Error fetching services page data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categories = ['All', ...services.map((s) => s.title).filter(Boolean)];

  const filteredServices = services.filter((service) => {
    const matchesCategory = activeFilter === 'All' || service.title === activeFilter;
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      searchTerm === '' ||
      service.title.toLowerCase().includes(q) ||
      service.desc.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  const heroStats = [
    { icon: <FaBuilding />, num: services.length || 5, suffix: '+', label: 'Business Verticals' },
    { icon: <FaGlobe />, num: 3, suffix: '+', label: 'Industry Sectors' },
    { icon: <FaChartLine />, num: 100, suffix: '%', label: 'Excellence Rate' },
  ];

  if (loading) {
    return (
      <div className="sg-loader">
        <div className="sg-loader-inner">
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <span className="sg-loader-text">SARWA GROUP</span>
        </div>
      </div>
    );
  }

  return (
    <main className="sg-services-page">
      {/* ══════════════════════════════════════════
          HERO SECTION
         ══════════════════════════════════════════ */}
      <section
        className="sg-services-hero"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop')`,
        }}
      >
        <div className="sg-services-hero-overlay"></div>

        {/* Decorative Elements */}
        <div className="sg-hero-decor">
          <div className="sg-hero-circle sg-hero-circle-1"></div>
          <div className="sg-hero-circle sg-hero-circle-2"></div>
          <div className="sg-hero-line sg-hero-line-1"></div>
          <div className="sg-hero-line sg-hero-line-2"></div>
        </div>

        <div className="container sg-services-hero-content">
          {/* Breadcrumb */}
          <div className="sg-breadcrumb">
            <Link to="/">Home</Link>
            <span className="sg-crumb-sep">/</span>
            <span className="sg-crumb-current">Our Businesses</span>
          </div>

          <div className="sg-hero-text-center">
            <div className="sg-hero-badge">
              <span className="sg-badge-dot"></span>
              <span>GROUP COMPANIES & VENTURES</span>
              <span className="sg-badge-dot"></span>
            </div>

            <h1 className="sg-services-hero-title">
              Diversified Verticals.
              <br />
              <span className="sg-gold-word">Uncompromising Value.</span>
            </h1>

            <div className="sg-hero-separator">
              <span className="sg-sep-line"></span>
              <span className="sg-sep-diamond"></span>
              <span className="sg-sep-line"></span>
            </div>

            <p className="sg-services-hero-desc">
              Explore our diverse portfolio of businesses driving sustainable growth, innovation, and excellence across multiple industry sectors.
            </p>

            {/* Hero Stats Mini */}
            <div className="sg-hero-stats-mini">
              {heroStats.map((s, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div className="sg-hsm-divider"></div>}
                  <div className="sg-hsm-item">
                    <div className="sg-hsm-icon">{s.icon}</div>
                    <div className="sg-hsm-text">
                      <strong>
                        {s.num}
                        {s.suffix}
                      </strong>
                      <span>{s.label}</span>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          FILTER & SEARCH BAR
         ══════════════════════════════════════════ */}
      <section className="sg-filter-section">
        <div className="container">
          <div className="sg-filter-bar">
            <div className="sg-filter-info">
              <span className="sg-filter-count">
                {filteredServices.length} {filteredServices.length === 1 ? 'Business' : 'Businesses'}
              </span>
              <span className="sg-filter-label">Available in Portfolio</span>
            </div>

            <div className="sg-filter-controls">
              <div className="sg-filter-buttons">
                {categories.map((cat, i) => (
                  <button
                    key={i}
                    className={`sg-filter-btn ${activeFilter === cat ? 'active' : ''}`}
                    onClick={() => setActiveFilter(cat)}
                  >
                    {cat === 'All' && <FaTh />}
                    <span>{cat}</span>
                  </button>
                ))}
              </div>

              <div className="sg-search-box">
                <FaSearch className="sg-search-icon" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="sg-clear-search" onClick={() => setSearchTerm('')}>
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          BUSINESS VERTICALS CARDS
         ══════════════════════════════════════════ */}
      <section className={`sg-verticals-section section-padding ${cardsVisible ? 'sg-visible' : ''}`} ref={cardsRef}>
        <div className="sg-verticals-bg"></div>

        <div className="container">
          <div className="sg-section-header-center">
            <span className="sg-section-tag">OUR PORTFOLIO</span>
            <h2 className="sg-section-heading">Explore Our Group Companies</h2>
            <p className="sg-section-subtext">
              Sarwa Group operates across diverse sectors, each subsidiary contributing to our shared vision of excellence and sustainable growth.
            </p>
          </div>

          {filteredServices.length > 0 ? (
            <div className="sg-verticals-grid">
              {filteredServices.map((s, i) => (
                <div
                  key={s.id || i}
                  className="sg-vertical-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="sg-vc-header">
                    <div className="sg-vc-icon-wrap">
                      <div className="sg-vc-icon">{s.iconNode}</div>
                      <div className="sg-vc-icon-glow"></div>
                    </div>
                    <span className="sg-vc-num">{String(i + 1).padStart(2, '0')}</span>
                  </div>

                  <div className="sg-vc-body">
                    <span className="sg-vc-tag">GROUP COMPANY</span>
                    <h3 className="sg-vc-title">{s.title}</h3>
                    <div className="sg-vc-line"></div>
                    <p className="sg-vc-desc">{s.desc}</p>

                    <div className="sg-vc-features">
                      {s.features.map((f, idx) => (
                        <div key={idx} className="sg-vc-feature">
                          <FaCheckCircle />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="sg-vc-footer">
                    <Link to={s.path} className="sg-vc-link">
                      <span>Explore Division</span>
                      <div className="sg-vc-link-arrow">
                        <FaArrowRight />
                      </div>
                    </Link>
                  </div>

                  <div className="sg-vc-accent-line"></div>
                  <div className="sg-vc-corner"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="sg-no-results">
              <div className="sg-no-results-icon">
                <FaSearch />
              </div>
              <h3>No Businesses Found</h3>
              <p>We couldn't find any businesses matching your search criteria. Please try different filters.</p>
              <button
                className="sg-btn-primary"
                onClick={() => {
                  setActiveFilter('All');
                  setSearchTerm('');
                }}
              >
                <span>Reset Filters</span>
                <FaArrowRight />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════════
          WHY OUR PORTFOLIO
         ══════════════════════════════════════════ */}
      <section className="sg-why-portfolio section-padding">
        <div className="container">
          <div className="sg-wp-grid">
            <div className="sg-wp-content">
              <span className="sg-section-tag">WHY SARWA GROUP</span>
              <h2 className="sg-section-heading">
                A Portfolio Built on <span className="sg-gold-word">Trust</span> &amp; <span className="sg-gold-word">Excellence</span>
              </h2>
              <div className="sg-heading-line"></div>
              <p className="sg-wp-desc">
                Every business under Sarwa Group operates with a shared commitment to quality, ethics, and long-term value creation. Our diversified portfolio ensures resilience and continuous innovation.
              </p>

              <div className="sg-wp-features">
                {[
                  { icon: <FaLightbulb />, title: 'Strategic Vision', desc: 'Forward-thinking approach to every venture we undertake.' },
                  { icon: <FaStar />, title: 'Quality Assured', desc: 'Rigorous standards maintained across all our subsidiaries.' },
                  { icon: <FaHandshake />, title: 'Trusted Partnerships', desc: 'Building long-term relationships with all stakeholders.' },
                  { icon: <FaChartLine />, title: 'Sustainable Growth', desc: 'Balanced expansion focused on lasting impact.' },
                ].map((f, i) => (
                  <div key={i} className="sg-wp-feature">
                    <div className="sg-wp-icon">{f.icon}</div>
                    <div>
                      <h4>{f.title}</h4>
                      <p>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sg-wp-visual">
              <div className="sg-wp-card">
                <div className="sg-wp-card-header">
                  <span>SARWA GROUP</span>
                  <div className="sg-wp-badge">
                    <FaStar /> Excellence
                  </div>
                </div>
                <h3>Diversified Excellence</h3>
                <p>
                  Building tomorrow's businesses today with unwavering commitment to values, quality, and sustainable growth across every sector we serve.
                </p>
                <div className="sg-wp-metrics">
                  <div className="sg-wp-metric">
                    <strong>{services.length}+</strong>
                    <span>Verticals</span>
                  </div>
                  <div className="sg-wp-metric">
                    <strong>100%</strong>
                    <span>Quality</span>
                  </div>
                  <div className="sg-wp-metric">
                    <strong>24/7</strong>
                    <span>Excellence</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          PARTNERS
         ══════════════════════════════════════════ */}
      {partners.length > 0 && (
        <section className="sg-partners">
          <div className="container">
            <p className="sg-partners-label">TRUSTED BY INDUSTRY LEADERS</p>
            <div className="sg-partners-track-wrap">
              <div className="sg-partners-track">
                {[...partners, ...partners, ...partners].map((p, i) => (
                  <div key={i} className="sg-partner-item">
                    <span>{p.name || p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default Services;